import { getPlatformServerUrl } from 'src/utils/env'
import { toUser, UserJSON } from 'src/models/User'
import { TranscriptOld, TranscriptJSON } from 'src/models/TranscriptOld'
import { toTranscriptMetadata, TranscriptMetadata, TranscriptMetadataJSON } from 'src/models/TranscriptMetadata'
import { rollbarService } from 'src/services/RollbarService'
import { analyticsService } from 'src/services/AnalyticsService'
import { MediaDescriptor, MediaDescriptorJSON } from 'src/models/Media'

import { HttpClientInterface } from './HttpClientInterface'
import { HttpClientBase } from './HttpClientBase'

export type HttpOptions = {
    baseUrl?: string
}

export class PlatformHttpClient extends HttpClientBase implements HttpClientInterface {
    constructor(options: HttpOptions = {}) {
        super({
            baseURL: options.baseUrl || `${getPlatformServerUrl()}/api/v1/`,
        })
    }

    async getTranscriptMetadata(transcriptId: string): Promise<TranscriptMetadata> {
        const startTime = performance.now()

        return this.get<TranscriptMetadataJSON>(`/transcription_jobs/${transcriptId}/configuration`, {
            withCredentials: true,
            requiresAuth: true,
        }).then((json) => {
            const metadata = toTranscriptMetadata(json)

            const endTime = performance.now()
            analyticsService.track('Performance - Get transcript metadata', {
                responseTime: endTime - startTime,
                sessionStartAt: metadata.sessionStartAt,
                sessionJoinedAt: new Date().toISOString(),
            })

            return metadata
        })
    }

    async getTranscript(transcriptId: string, languageCode?: string): Promise<TranscriptOld> {
        const params = languageCode ? { language_code: languageCode } : {}
        const startTime = performance.now()

        return this.get<TranscriptJSON>(`/transcription_jobs/${transcriptId}/transcript`, {
            withCredentials: true,
            requiresAuth: true,
            params,
        }).then((json) => {
            if (TranscriptOld.isValidJSON(json)) {
                const transcript = TranscriptOld.fromJSON(json)

                const endTime = performance.now()
                analyticsService.track('Performance - Get transcript', {
                    responseTime: endTime - startTime,
                    wordsAmount: json.data.words.length,
                })

                return transcript
            }

            console.warn('[HttpClient] Invalid transcript JSON', json)
            rollbarService.error(`[HttpClient] Invalid transcript JSON: ${json}`)

            return TranscriptOld.EMPTY
        })
    }

    async getMedia(transcriptJobId: string): Promise<MediaDescriptor> {
        return this.get<MediaDescriptorJSON>(`/transcription_jobs/${transcriptJobId}/media`, {
            withCredentials: true,
        }).then(MediaDescriptor.fromJSON)
    }

    async identify() {
        return this.get<UserJSON>('/users/whoami', { withCredentials: true }).then(toUser)
    }
}
